import React, { useState, useRef } from 'react';
import { Mail, Download, ChevronDown } from 'lucide-react';
import { ButtonV2, ButtonV2Props } from './index';
import { cn } from '../../../../lib/utils';

// Dropdown option interface
export interface DropdownOption {
  value: string;
  label: string;
  description?: string;
}

// Dropdown Menu Component
interface DropdownMenuProps {
  options: DropdownOption[];
  selectedValue: string;
  onSelect: (value: string) => void;
  onClose: () => void;
}

const DropdownMenu: React.FC<DropdownMenuProps> = ({
  options,
  selectedValue,
  onSelect,
  onClose,
}) => {
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleSelect = (value: string) => {
    onSelect(value);
    onClose();
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-10 bg-transparent"
        onClick={handleBackdropClick}
      />

      {/* Dropdown Menu */}
      <div className="absolute right-0 top-[calc(100%+4px)] z-20 flex flex-col items-end w-56 p-1 bg-white border border-gray-200 rounded-lg shadow-md">
        {options.map((option) => (
          <button
            key={option.value}
            className={cn(
              'flex items-center w-full h-8 px-2 py-1.5 gap-2 bg-transparent border-none rounded text-left cursor-pointer transition-colors duration-200 hover:bg-gray-100',
              selectedValue === option.value && 'bg-gray-100'
            )}
            onClick={() => handleSelect(option.value)}
          >
            <div className="flex flex-col items-start w-full">
              <div className="text-xs font-medium leading-5 text-gray-900 whitespace-nowrap">
                {option.label}
              </div>
              {option.description && (
                <div className="text-xs font-normal leading-4 text-gray-500 whitespace-nowrap">
                  {option.description}
                </div>
              )}
            </div>
          </button>
        ))}
      </div>
    </>
  );
};

// Action type discriminated union
export type ActionType = 'DEFAULT' | 'EMAIL' | 'DROPDOWN';

interface BaseActionButtonProps {
  text: string;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  disabled?: boolean;
  variant?: ButtonV2Props['variant'];
  size?: ButtonV2Props['size'];
  className?: string;
  loading?: boolean;
}

interface DefaultActionProps extends BaseActionButtonProps {
  actionType?: 'DEFAULT';
  onClick: () => void;
}

interface EmailActionProps extends BaseActionButtonProps {
  actionType: 'EMAIL';
  onClick: () => void;
}

interface DropdownActionProps extends BaseActionButtonProps {
  actionType: 'DROPDOWN';
  onSelect: (value: string) => void;
  options: DropdownOption[];
  selectedValue?: string;
  onSelectedChange?: (value: string) => void;
}

export type ActionButtonProps = DefaultActionProps | EmailActionProps | DropdownActionProps;

const ActionButtonV2: React.FC<ActionButtonProps> = (props) => {
  const {
    text,
    icon,
    iconPosition = 'left',
    disabled = false,
    actionType = 'DEFAULT',
    variant = 'primary',
    size = 'md',
    className,
    loading,
  } = props;

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [internalSelectedValue, setInternalSelectedValue] = useState<string>(
    props.actionType === 'DROPDOWN' ? props.options[0]?.value || '' : 'pdf'
  );
  const containerRef = useRef<HTMLDivElement>(null);

  // Determine icon based on action type
  const getIcon = () => {
    if (icon) return icon;
    
    switch (actionType) {
      case 'EMAIL':
        return <Mail size={16} />;
      case 'DROPDOWN':
        return <Download size={16} />;
      default:
        return null;
    }
  };

  const buttonIcon = getIcon();

  // Handle click based on action type
  const handleClick = () => {
    if (props.actionType === 'DROPDOWN') {
      setIsDropdownOpen(!isDropdownOpen);
    } else {
      props.onClick();
    }
  };

  // Handle dropdown selection
  const handleDropdownSelect = (value: string) => {
    if (props.actionType !== 'DROPDOWN') return;
    
    if (!props.selectedValue && props.onSelectedChange) {
      props.onSelectedChange(value);
    }
    if (!props.selectedValue) {
      setInternalSelectedValue(value);
    }
    
    props.onSelect(value);
  };

  const currentSelectedValue = 
    props.actionType === 'DROPDOWN' 
      ? (props.selectedValue || internalSelectedValue)
      : 'pdf';

  return (
    <div ref={containerRef} className="relative inline-block">
      <ButtonV2
        variant={variant}
        size={size}
        onClick={handleClick}
        disabled={disabled}
        loading={loading}
        prefix={iconPosition === 'left' ? buttonIcon : undefined}
        suffix={iconPosition === 'right' ? buttonIcon : undefined}
        className={className}
      >
        {text}
      </ButtonV2>

      {/* Dropdown menu - only for DROPDOWN action type */}
      {props.actionType === 'DROPDOWN' && isDropdownOpen && (
        <DropdownMenu
          options={props.options}
          selectedValue={currentSelectedValue}
          onSelect={handleDropdownSelect}
          onClose={() => setIsDropdownOpen(false)}
        />
      )}
    </div>
  );
};

export default ActionButtonV2;
